import { useEffect, useRef } from 'react';
import { selectIsModalOpened } from 'state/selectors';
import { ModalName } from 'features/Modals/ModalProvider';
import { useWaitingRoom } from 'features/AccessRequests/hooks/useWaitingRoom';
import { accessRequestsSettingsStore } from 'state/store';
import { getAccessRequestSoundUrl } from 'features/AccessRequests/utils/accessRequestSound';
import { isWaitingRequestSoundSuppressed } from 'features/AccessRequests/utils/waitingListRegistry';
import { getLogger, LOGGERS } from 'lib/logger';

const logger = getLogger(LOGGERS.WaitingRoom);

const playAccessRequestSound = () => {
    const audio = new Audio(getAccessRequestSoundUrl());
    audio.play().catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
            logger.warn('[waiting-room] sound blocked by autoplay policy');
        } else {
            logger.warn('[waiting-room] failed to play access request sound', error);
        }
    });
};

export const useWaitingRoomNotifications = () => {
    const { waitingUsers, isCanManageWaitingUsers } = useWaitingRoom();
    const { soundEnabled } = accessRequestsSettingsStore;
    const seenWaitingIdsRef = useRef<Set<string>>(new Set());
    const soundEnabledRef = useRef(soundEnabled);
    const isInitializedRef = useRef(false);
    const isParticipantsOpen = selectIsModalOpened(ModalName.PARTICIPANTS);

    useEffect(() => {
        const currentIds = new Set(waitingUsers.map((u) => u.waitingListId));

        for (const id of seenWaitingIdsRef.current) {
            if (!currentIds.has(id)) {
                seenWaitingIdsRef.current.delete(id);
            }
        }

        if (!isInitializedRef.current) {
            isInitializedRef.current = true;
            currentIds.forEach((id) => seenWaitingIdsRef.current.add(id));
            soundEnabledRef.current = soundEnabled;
            return;
        }

        if (soundEnabled && !soundEnabledRef.current) {
            currentIds.forEach((id) => seenWaitingIdsRef.current.add(id));
        }
        soundEnabledRef.current = soundEnabled;

        const newUsers = waitingUsers.filter(
            (u) => !seenWaitingIdsRef.current.has(u.waitingListId) && !isWaitingRequestSoundSuppressed(u.waitingListId),
        );
        const hasNewRequests = newUsers.length > 0;

        if (isCanManageWaitingUsers && hasNewRequests && !isParticipantsOpen && soundEnabled) {
            playAccessRequestSound();
            newUsers.forEach((u) => seenWaitingIdsRef.current.add(u.waitingListId));
        }
    }, [waitingUsers, isCanManageWaitingUsers, soundEnabled, isParticipantsOpen]);
};
